import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { cancelOnboarding, type OnboardingStep, STEP_TO_PROGRESS } from '@/api/onboarding'
import { track } from '@/lib/analytics'

interface Props {
  step: OnboardingStep
  children: ReactNode
  footer?: ReactNode
  variant?: 'full' | 'minimal' | 'final'
}

const TOTAL_PIPS = 8

export function OnboardingShell({ step, children, footer, variant = 'full' }: Props) {
  const [showCancel, setShowCancel] = useState(false)
  const navigate = useNavigate()
  const stepProgress = STEP_TO_PROGRESS[step]

  // Safety-net для magic_login_ok: всегда эмитим из shell-mount если есть
  // campaignId в sessionStorage. В normal-path этот event ДУБЛИРУЕТ тот что
  // AuthContext уже отправил (source='auth-context'). В edge-cases (MAX in-app
  // browser режет fetch на unload, или AuthContext не запустился) — это
  // единственный event который доходит. Дедуп выполняется на read-side через
  // count DISTINCT sessionId, поэтому дубль не искажает funnel-цифры.
  // Свой sessionStorage-флаг — чтобы не дублировать на каждом step-mount внутри
  // одной сессии онбординга.
  useEffect(() => {
    try {
      const shellAlreadyFired = sessionStorage.getItem('tgx-magic-login-shell-fired')
      const campaignId = sessionStorage.getItem('tgx-magic-campaign')
      if (!shellAlreadyFired && campaignId) {
        sessionStorage.setItem('tgx-magic-login-shell-fired', '1')
        track('magic_login_ok', {
          campaignId,
          source: 'shell-fallback',
          firstStep: step,
          message: `magic-login ok (shell-fallback) · entered onboarding at ${step}`,
        })
      }
    } catch {}
  }, [step])

  // Трекаем просмотр каждого шага онбординга — централизованно, чтобы
  // не править 15 страниц. Цепляется к моменту маунта компонента шага.
  useEffect(() => {
    track('onboarding_step_view', {
      step,
      progress: stepProgress,
      message: `onboarding · ${step} (${stepProgress}/${TOTAL_PIPS})`,
    })
  }, [step, stepProgress])

  async function handleConfirmCancel() {
    track('onboarding_cancelled', {
      step,
      progress: stepProgress,
      message: `отмена онбординга · на шаге ${step} (${stepProgress}/${TOTAL_PIPS})`,
    })
    try {
      await cancelOnboarding()
    } catch {}
    navigate('/dashboard', { replace: true })
  }

  return (
    <div className="onb-slide">
      <header className={`onb-top ${variant === 'minimal' ? 'minimal' : ''}`}>
        <img src="/assets/tgx-logo.svg" alt="TeleGraphyx" className="onb-logo" />
        {variant === 'minimal' && <span className="onb-app-tag">кабинет</span>}
        <div className="onb-top-spacer" />
        {variant === 'full' && (
          <>
            {variant === 'full' && step !== 'final' && (
              <span className="onb-top-step">
                Шаг&nbsp;<b>{stepProgress}</b>&nbsp;из&nbsp;<b>{TOTAL_PIPS}</b>
              </span>
            )}
            <div className="onb-progress">
              {Array.from({ length: TOTAL_PIPS }).map((_, i) => {
                const idx = i + 1
                const cls = idx < stepProgress ? 'done' : idx === stepProgress ? 'active' : ''
                return <div key={i} className={`onb-progress-pip ${cls}`} />
              })}
            </div>
          </>
        )}
        {variant === 'final' && (
          <span className="onb-top-step">
            Готово&nbsp;·&nbsp;<b>{TOTAL_PIPS}</b>&nbsp;из&nbsp;<b>{TOTAL_PIPS}</b>
          </span>
        )}
      </header>

      <main className="onb-main">{children}</main>

      {footer && <footer className="onb-foot">{footer}</footer>}

      {showCancel && <CancelPopup onClose={() => setShowCancel(false)} onConfirm={handleConfirmCancel} />}

      <style>{`
        .onb-slide {
          min-height: 100vh;
          background:
            radial-gradient(ellipse 60% 50% at 50% -10%, rgba(0, 128, 255, 0.06), transparent 60%),
            var(--tg-paper, #fafafd);
          display: grid;
          grid-template-rows: auto 1fr auto;
          color: var(--tg-ink, #111);
          font-family: var(--tg-font, Inter, sans-serif);
        }
        .onb-top {
          padding: 22px 48px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-sizing: border-box;
        }
        .onb-top.minimal {
          gap: 12px;
        }
        .onb-logo { height: 28px; }
        .onb-app-tag {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
          padding: 4px 8px;
          background: var(--tg-surface-2, #f5f5f9);
          border-radius: 6px;
        }
        .onb-top-spacer { flex: 1; }
        .onb-top-step {
          font-size: 13px;
          font-weight: 600;
          color: var(--tg-mute, #8f9eb0);
          font-variant-numeric: tabular-nums;
        }
        .onb-top-step b { color: var(--tg-ink, #111); font-weight: 700; }
        .onb-progress {
          display: flex;
          gap: 4px;
          align-items: center;
        }
        .onb-progress-pip {
          height: 4px;
          width: 22px;
          border-radius: 2px;
          background: var(--tg-line-2, #f0f0f4);
          transition: background 200ms, width 200ms;
        }
        .onb-progress-pip.done { background: var(--tg-up, #00a860); }
        .onb-progress-pip.active {
          background: var(--tg-blue, #0080ff);
          width: 44px;
        }
        .onb-top-link {
          font-size: 13px;
          font-weight: 600;
          color: var(--tg-mute, #8f9eb0);
          text-decoration: none;
          padding: 8px 14px;
          border-radius: 8px;
          background: transparent;
          border: 0;
          font-family: inherit;
          cursor: pointer;
        }
        .onb-top-link:hover {
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-ink, #111);
        }
        .onb-main {
          max-width: 1100px;
          margin: 0 auto;
          /* нижний padding с запасом под fixed-футер, чтобы контент не прятался */
          padding: 16px 48px 112px;
          width: 100%;
          box-sizing: border-box;
        }
        .onb-foot {
          padding: 18px 48px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-top: 1px solid var(--tg-line-2, #f0f0f4);
          /* Fixed к низу вьюпорта — кнопка CTA всегда видна, даже если контент
             длиннее экрана (особенно важно на TariffPage перед оплатой).
             .onb-main компенсирует высоту через padding-bottom. */
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 20;
          background: rgba(255,255,255,0.92);
          backdrop-filter: blur(12px);
          box-sizing: border-box;
        }
        .stage-eyebrow {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--tg-blue, #0080ff);
          margin: 0 0 14px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .stage-eyebrow.center { justify-content: center; }
        .stage-eyebrow-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0066cc);
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.06em;
        }
        .stage-eyebrow-pill.success {
          background: rgba(0,168,96,0.12);
          color: var(--tg-up, #00a860);
        }
        .stage-eyebrow-pill svg {
          width: 12px;
          height: 12px;
        }
        .stage-title {
          font-weight: 800;
          font-size: 44px;
          line-height: 1.05;
          letter-spacing: -0.025em;
          color: var(--tg-ink, #111);
          margin: 0 0 16px;
        }
        .stage-title.center { text-align: center; }
        .stage-title em {
          font-family: 'Instrument Serif', serif;
          font-style: italic;
          font-weight: 400;
          color: var(--tg-blue, #0080ff);
          letter-spacing: 0;
          font-size: 0.94em;
        }
        .stage-sub {
          font-size: 17px;
          line-height: 1.55;
          color: var(--tg-ink-2, #333);
          margin: 0 0 32px;
          max-width: 640px;
        }
        .stage-sub.center { text-align: center; margin-left: auto; margin-right: auto; }
        .stage-sub b { color: var(--tg-ink, #111); font-weight: 700; }
        .foot-back, .foot-next, .foot-skip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 18px;
          font-family: inherit;
          font-weight: 600;
          font-size: 14px;
          border-radius: 10px;
          cursor: pointer;
          border: 0;
          background: transparent;
          transition: background 200ms, color 200ms;
        }
        .foot-back, .foot-skip { color: var(--tg-mute, #8f9eb0); }
        .foot-back:hover, .foot-skip:hover {
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-ink, #111);
        }
        .foot-spacer { flex: 1; }
        .foot-next {
          background: var(--tg-blue, #0080ff);
          color: white;
          font-weight: 700;
          padding: 12px 22px;
        }
        .foot-next:hover { background: var(--tg-blue-hover, #0066cc); }
        .foot-next:disabled {
          background: var(--tg-line-3, #d0d0d8);
          color: white;
          cursor: not-allowed;
        }
        .foot-back svg, .foot-next svg { width: 14px; height: 14px; }

        /* ─── Mobile (<= 720px) ─── */
        @media (max-width: 720px) {
          .onb-top {
            padding: 14px 16px;
            gap: 8px;
            flex-wrap: wrap;
          }
          .onb-logo { height: 24px; }
          .onb-top-step {
            font-size: 12px;
            order: 3;
            width: 100%;
            text-align: center;
            padding-top: 8px;
          }
          .onb-progress {
            order: 4;
            width: 100%;
            justify-content: center;
          }
          .onb-progress-pip { width: 14px; }
          .onb-progress-pip.active { width: 30px; }
          .onb-top-link {
            font-size: 12px;
            padding: 6px 10px;
            order: 2;
          }
          .onb-top-spacer { display: none; }

          .onb-main {
            padding: 12px 16px 96px;
          }
          .onb-foot {
            padding: 12px 16px;
            gap: 8px;
            flex-wrap: nowrap;
            align-items: center;
          }
          .stage-title { font-size: 28px; }
          .stage-sub { font-size: 15px; }
          .foot-next, .foot-back, .foot-skip {
            padding: 9px 10px;
            font-size: 12.5px;
            white-space: nowrap;
            min-width: 0;
          }
          .foot-back, .foot-next { flex: 0 0 auto; }
          .foot-skip {
            flex: 1 1 auto;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .foot-spacer {
            display: block;
            flex: 1;
            min-width: 8px;
          }
          .foot-meta {
            min-width: 0;
            padding: 0;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        }
        @media (max-width: 420px) {
          .stage-title { font-size: 24px; }
          .foot-next, .foot-back, .foot-skip { padding: 8px 8px; font-size: 12px; }
        }
      `}</style>
    </div>
  )
}

function CancelPopup({ onClose, onConfirm }: { onClose: () => void; onConfirm: () => void }) {
  return (
    <div className="onb-popup-veil" onClick={onClose}>
      <div className="onb-popup" onClick={(e) => e.stopPropagation()}>
        <div className="onb-popup-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16v.01" />
          </svg>
        </div>
        <h3 className="onb-popup-title">Отменить настройку?</h3>
        <p className="onb-popup-text">
          Вы действительно хотите отменить настройку? <b>Изменения не будут сохранены</b>, но подключённый канал останется.
        </p>
        <div className="onb-popup-actions">
          <button className="onb-popup-stay" onClick={onClose}>
            Остаться
          </button>
          <button className="onb-popup-cancel" onClick={onConfirm}>
            Отменить настройку
          </button>
        </div>
      </div>

      <style>{`
        .onb-popup-veil {
          position: fixed;
          inset: 0;
          background: rgba(38, 40, 45, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: veil-in 200ms ease-out;
        }
        @keyframes veil-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .onb-popup {
          background: white;
          border-radius: 16px;
          padding: 28px;
          max-width: 420px;
          width: calc(100% - 32px);
          text-align: center;
          box-shadow: 0 24px 60px -16px rgba(38,40,45,0.3);
          animation: popup-in 200ms ease-out;
        }
        @keyframes popup-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .onb-popup-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: rgba(255, 213, 80, 0.16);
          color: rgb(180, 130, 0);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }
        .onb-popup-icon svg { width: 28px; height: 28px; }
        .onb-popup-title {
          margin: 0 0 8px;
          font-size: 20px;
          font-weight: 800;
          color: var(--tg-ink, #111);
        }
        .onb-popup-text {
          margin: 0 0 24px;
          font-size: 14px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
        }
        .onb-popup-text b { color: var(--tg-ink, #111); }
        .onb-popup-actions {
          display: flex;
          gap: 8px;
        }
        .onb-popup-stay, .onb-popup-cancel {
          flex: 1;
          padding: 12px 16px;
          border: 0;
          border-radius: 10px;
          font-family: inherit;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
        }
        .onb-popup-stay {
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-ink, #111);
        }
        .onb-popup-stay:hover { background: var(--tg-line-2, #f0f0f4); }
        .onb-popup-cancel {
          background: #ff4000;
          color: white;
        }
        .onb-popup-cancel:hover { background: #d63500; }
      `}</style>
    </div>
  )
}
